import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import { BULLMQ_JOBS } from '../../constant/message-queue.constant';
import { IErrorResponse } from '../../interface/main.interface';
import { IUploadFileParams, IUploadFileResponse } from '../../interface/bulkUpload.interface';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';
import { bulkUploadService } from '../../services/bulkUpload';

/**
 * Download bulk upload template for specified module
 * @param moduleKey - The module identifier (e.g., BULLMQ_JOBS.GUARDIAN_BULK_UPLOAD)
 * @returns Promise<void> - Triggers file download in browser
 */
export const downloadTemplate = createAsyncThunk<
  void,
  BULLMQ_JOBS,
  { rejectValue: IErrorResponse }
>('bulkUpload/downloadTemplate', async (moduleKey, { rejectWithValue }) => {
  try {
    await bulkUploadService.downloadTemplate(moduleKey);
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_download_template', 'Failed to download template.')));
  }
});

/**
 * Upload bulk data file for specified module
 * @param params - Upload parameters containing moduleKey and formData
 * @returns Promise<IUploadFileResponse> - Upload result with status and message
 */
export const uploadFile = createAsyncThunk<
  IUploadFileResponse,
  IUploadFileParams,
  { rejectValue: IErrorResponse }
>('bulkUpload/uploadFile', async ({ moduleKey, formData }, { rejectWithValue }) => {
  try {
    const response = await bulkUploadService.uploadFile(moduleKey, formData);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_upload_file_2', 'Failed to upload file.')));
  }
});

/**
 * Grouped bulk upload actions for easy import
 */
export const bulkUploadActions = {
  downloadTemplate,
  uploadFile,
};
